import React, { useEffect, useState } from 'react';
import { Inputs, PackageType, BuildPayType, IndustryType, RevenueCadence, salesPerNinetyDayRep, withCumulativeRampSales } from '../utils/roiCalculations';
import { commissionsModeled, managerView } from '../utils/commissions';
import { SummerPestSalesInputs, withVeteranRate } from './SummerPestSalesInputs';
import { SUMMER_PEST, withIndustry } from '../utils/summerProgram';
import { CommissionInputs } from './CommissionInputs';
interface CalculatorInputsProps {
  inputs: Inputs;
  onChange: (newInputs: Inputs) => void;
  onReset?: () => void;
  onReview?: () => void;
  showActionButtons?: boolean;
}
export function CalculatorInputs({
  inputs,
  onChange,
  onReset,
  onReview,
  showActionButtons = true
}: CalculatorInputsProps) {
  const [expandedSections, setExpandedSections] = useState({
    companyInfo: true,
    campaignAdSpend: false,
    revenueMargins: false,
    salesPerformance: false,
    retentionModel: false
  });
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleChange = (field: keyof Inputs, value: string | number) => {
    onChange({
      ...inputs,
      [field]: value
    });
  };
  // Handle numeric input changes - allow empty string, convert to number only when valid
  const handleNumericChange = (field: keyof Inputs, value: string) => {
    // If empty, set to empty string to allow clearing
    if (value === '') {
      onChange({
        ...inputs,
        [field]: ''
      });
      return;
    }
    // Otherwise parse as float
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      onChange({
        ...inputs,
        [field]: numValue
      });
    }
  };
  // Cumulative first-90-days sales is a view of the ramp curve: editing it
  // rescales the three monthly percentages, and it re-derives whenever the
  // veteran rate changes.
  const [rampTotalDraft, setRampTotalDraft] = useState<string | null>(null);
  const commitRampTotal = () => {
    if (rampTotalDraft === null) return;
    const parsed = parseFloat(rampTotalDraft);
    setRampTotalDraft(null);
    if (Number.isNaN(parsed)) return;
    onChange(withCumulativeRampSales(inputs, Math.max(0, parsed)));
  };
  // Update industry-specific defaults when industry changes
  // Summer Pest also switches on the summer program and a Month 4 launch.
  const handleIndustryChange = (newIndustry: IndustryType) => onChange(withIndustry(inputs, newIndustry));
  const isSummerPest = inputs.industry === SUMMER_PEST;
  return <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6 shadow-lg max-w-4xl mx-auto" data-id="element-307" data-mp="1fgh33gzwe99w">
      <div className="space-y-4" data-id="element-308" data-mp="1kfagl9f6q0a2">
        {/* Company Information - Single Row (No Collapsible) */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-309" data-mp="285e84if3m9kp">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-310" data-mp="2cxeaehin5ekg">
            {/* Company Name */}
            <div data-id="element-311" data-mp="1h43r19qz9u4">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-312" data-mp="1gmb0emandqy">
                Company Name
              </label>
              <input type="text" value={inputs.companyName} onChange={e => handleChange('companyName', e.target.value)} placeholder="Enter company name" className="w-full p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-313" data-mp="3sm463c065t17" />
            </div>

            {/* Industry */}
            <div data-id="element-314" data-mp="3npn71mrflu9c">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-315" data-mp="3nq6q56stiooy">
                Industry
              </label>
              <select value={inputs.industry} onChange={e => handleIndustryChange(e.target.value as IndustryType)} className="w-full p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-316" data-mp="3iopq1di5r69f">
                <option value="" data-id="element-317" data-mp="1y8jk21d617r0">Select</option>
                <option data-id="element-318" data-mp="1y9bfnc6rj5ha">Windows</option>
                <option data-id="element-319" data-mp="238fn9ye1gv5i">Roofing</option>
                <option data-id="element-320" data-mp="237cvlo8w6ooj">Solar (Sales)</option>
                <option data-id="element-321" data-mp="28794o1wg56ay">Solar (Vertical)</option>
                <option data-id="element-322" data-mp="2880mm9x4f5ir">Solar (Community)</option>
                <option data-id="element-323" data-mp="2c8uax4eh6mc6">Pest</option>
                <option data-id="element-324" data-mp="2c7t1f4d3y7wk">Summer Pest</option>
                <option data-id="element-325" data-mp="2c6rlalh8iv7z">Fiber</option>
                <option data-id="element-326" data-mp="2h7fg7i8l3w7h">Deregulated Energy</option>
                <option data-id="element-327" data-mp="2h5ui5in29v4v">Hardscaping</option>
                <option data-id="element-328" data-mp="2ly45qs0pej3z">Landscaping</option>
                <option data-id="element-329" data-mp="2lzfjnknv578t">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Retention Modeling - Collapsible */}
        <div data-id="element-330" data-mp="2vxd4dy6iklwo">
          <div className="flex items-center justify-between p-3.5 bg-purple-50 rounded-lg cursor-pointer hover:bg-purple-100 transition-colors border border-purple-200" onClick={() => toggleSection('retentionModel')} data-id="element-331" data-mp="2vxv08tp9ky42">
            <h3 className="text-sm md:text-base font-semibold text-navy" data-id="element-332" data-mp="o8qn0pask7ed">
              Retention Modeling
            </h3>
            <span className="text-navy font-bold text-base" data-id="element-333" data-mp="f9vdb38jgf6b">
              {expandedSections.retentionModel ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.retentionModel && <div className="mt-3 bg-purple-50 p-3 rounded border-2 border-purple-200" data-id="element-334" data-mp="17zkjdv03v5es">
              <div className="space-y-2.5 flex flex-col items-center" data-id="element-335" data-mp="1307ruwyaha5v">
                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-336" data-mp="132bw5g03ob6o">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-337" data-mp="y7oi6b2q2z6c">
                    Hired to Launched
                  </label>
                  <div className="w-56 relative" data-id="element-338" data-mp="1qq27fm9ztctt">
                    <input type="number" min="0" max="100" step="1" value={inputs.pctLaunched} onChange={e => handleNumericChange('pctLaunched', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-339" data-mp="1qqv6e12pmy1e" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-340" data-mp="2wiooibfhg06q">
                      %
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-341" data-mp="2nijr8u406i6a">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-342" data-mp="2nkd0mh603ghz">
                    Launched to Day 30
                  </label>
                  <div className="w-56 relative" data-id="element-343" data-mp="lrylfpuucq59">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct30} onChange={e => handleNumericChange('pct30', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-344" data-mp="qpq9u9wa1u4p" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-345" data-mp="1jjn3gsagd06v">
                      %
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-346" data-mp="1sl4e3v53c9o6">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-347" data-mp="1ojiy40x9ff9r">
                    Day 30 to Day 90
                  </label>
                  <div className="w-56 relative" data-id="element-348" data-mp="3ucpv49vcxiud">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct90} onChange={e => handleNumericChange('pct90', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-349" data-mp="3qb4dkb7vogdu" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-350" data-mp="3vxggau80mn77">
                      %
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-351" data-mp="8r7hp9gkejw7">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-352" data-mp="drbyl0ldxgmy">
                    Day 90 to 1 Year
                  </label>
                  <div className="w-56 relative" data-id="element-353" data-mp="1k98h5ygt512o">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct1y} onChange={e => handleNumericChange('pct1y', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-354" data-mp="1p5fq804i2sou" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-355" data-mp="1gkh3uvqk5emo">
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Sales Performance - Collapsible */}
        <div data-id="element-356" data-mp="12lpxjvhxsx93">
          <div className="flex items-center justify-between p-3.5 bg-amber-50 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors border border-amber-200" onClick={() => toggleSection('salesPerformance')} data-id="element-357" data-mp="12m9f3b3odjml">
            <h3 className="text-sm md:text-base font-semibold text-navy" data-id="element-358" data-mp="1stj32aa99t3n">
              Sales Performance
            </h3>
            <span className="text-navy font-bold text-base" data-id="element-359" data-mp="1ospj1rzxoyg1">
              {expandedSections.salesPerformance ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.salesPerformance && <div className="mt-3 bg-amber-50 p-3 rounded border-2 border-amber-200" data-id="element-360" data-mp="1xrko30r8s2hr">
              <div className="space-y-2.5 flex flex-col items-center" data-id="element-361" data-mp="1a4cjkl1n0xgs">
                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-362" data-mp="1561awdn2wt05">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-363" data-mp="1jtmf6avowy9n">
                    Monthly Sales per Veteran
                  </label>
                  <input type="number" min="0" step="0.5" value={inputs.salesVetMonthly} onChange={e => {
                const v = parseFloat(e.target.value);
                if (isSummerPest && !Number.isNaN(v) && v > 0) {
                  onChange(withVeteranRate(inputs, v));
                  return;
                }
                handleNumericChange('salesVetMonthly', e.target.value);
              }} className="w-56 p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-364" data-mp="1f12xp2tvtizk" />
                </div>

                {isSummerPest && <SummerPestSalesInputs inputs={inputs} onChange={onChange} data-id="element-365" data-mp="3lqn4fld4cd2l" />}

                {!isSummerPest && <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-366" data-mp="3cqq7kwgks7wg">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-367" data-mp="3csun6bnu6vck">
                    Total Sales in First 90 Days
                  </label>
                  <input type="number" min="0" step={isSummerPest ? 5 : 0.5} value={rampTotalDraft ?? Math.round(salesPerNinetyDayRep(inputs) * 10) / 10} onChange={e => setRampTotalDraft(e.target.value)} onBlur={commitRampTotal} onKeyDown={e => {
                if (e.key === 'Enter') e.currentTarget.blur();
              }} title="Spread across the first three months using the ramp percentages in Assumptions" className="w-56 p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-368" data-mp="21cmedshtansg" />
                </div>}

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-369" data-mp="18kgiq7fdi0w7">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-370" data-mp="18idqdgqqzvob">
                    Pullthrough / Realization
                  </label>
                  <div className="w-56 relative" data-id="element-371" data-mp="2mfyhdb7mjo7d">
                    <input type="number" min="0" max="100" step="1" value={inputs.pullthrough} onChange={e => handleNumericChange('pullthrough', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-372" data-mp="2rfkxptir1kke" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-373" data-mp="2chis7ds5qp0i">
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Revenue & Margins - Collapsible */}
        <div data-id="element-374" data-mp="3sv8zetekpted">
          <div className="flex items-center justify-between p-3.5 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors border border-green-200" onClick={() => toggleSection('revenueMargins')} data-id="element-375" data-mp="3sywsxsk0qft3">
            <h3 className="text-sm md:text-base font-semibold text-navy" data-id="element-376" data-mp="4b75yrs96aqs">
              Revenue & Margins
            </h3>
            <span className="text-navy font-bold text-base" data-id="element-377" data-mp="8kckmfxohrh">
              {expandedSections.revenueMargins ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.revenueMargins && <div className="mt-3 bg-green-50 p-3 rounded border-2 border-green-200" data-id="element-378" data-mp="99ig73mgmn9j">
              <div className="space-y-2.5 flex flex-col items-center" data-id="element-379" data-mp="nxmp5fvpnkf4">
                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-380" data-mp="izbgh8h5jfyh">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-381" data-mp="j27gd2ck8epk">
                    Revenue Cadence
                  </label>
                  <select value={inputs.revenueCadence} onChange={e => handleChange('revenueCadence', e.target.value as RevenueCadence)} className="w-56 p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-382" data-mp="s1uftilqbafz">
                    <option value="one-time" data-id="element-383" data-mp="svbkljwlsc0g">One-Time Sale</option>
                    <option value="monthly" data-id="element-384" data-mp="su0jjrujlid1">Monthly Residual</option>
                    <option value="quarterly" data-id="element-385" data-mp="xo4roefjnwfp">Quarterly Residual</option>
                  </select>
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-386" data-mp="ivks1nrls730">
                  <label htmlFor="calc-launchMonth" className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-387" data-mp="nxa3v5mngos5">
                    Launch Month
                  </label>
                  <select id="calc-launchMonth" value={Number(inputs.launchMonth) || 0} onChange={e => handleChange('launchMonth', Number(e.target.value))} title="Reps hired before this month start their Month 1 sales here. Reps hired in it or later sell from their hire month as usual." className="w-56 p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-388" data-mp="exvds7aeag86">
                    <option value={0} data-id="element-389" data-mp="2uiwkgikw5r1s">None (sell from hire month)</option>
                    {Array.from({
                  length: 12
                }, (_, i) => i + 1).map(m => <option key={m} value={m} data-id="element-390" data-mp="2lj9k803werab">
                        Month {m}
                      </option>)}
                  </select>
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-391" data-mp="1a4mk0ah0w372">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-392" data-mp="3lshoi5da5hby">
                    {inputs.revenueCadence === 'one-time' ? 'Average Ticket (Revenue per Sale)' : 'Initial Sale Ticket'}
                  </label>
                  <div className="w-56 relative" data-id="element-393" data-mp="3ur33xrnggg8i">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-394" data-mp="3ervauad52uo">
                      $
                    </span>
                    <input type="number" min="0" step="500" value={inputs.ticket} onChange={e => handleNumericChange('ticket', e.target.value)} className="w-full p-2 pl-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-395" data-mp="dcpjxiw4x1nt" />
                  </div>
                </div>

                {inputs.revenueCadence !== 'one-time' && <>
                    <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-396" data-mp="209xikgwc2w8d">
                      <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-397" data-mp="2e0srft6u1dby">
                        {inputs.revenueCadence === 'monthly' ? 'Monthly Residual Ticket' : 'Quarterly Residual Ticket'}
                      </label>
                      <div className="w-56 relative" data-id="element-398" data-mp="1hajiribxar9y">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-399" data-mp="1hbuuq5fiv5c5">
                          $
                        </span>
                        <input type="number" min="0" step="10" value={inputs.residualTicket || 0} onChange={e => handleNumericChange('residualTicket', e.target.value)} className="w-full p-2 pl-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-400" data-mp="1qa6ky6ch8142" />
                      </div>
                    </div>

                    {inputs.revenueCadence === 'monthly' && <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-401" data-mp="3t09aiy6xc9m3">
                        <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-402" data-mp="1tolfk0fjbrp">
                          Monthly Attrition
                        </label>
                        <div className="w-56 relative" data-id="element-403" data-mp="3o5vjt937iqnr">
                          <input type="number" min="0" max="100" step="1" value={inputs.monthlyAttrition || 2} onChange={e => handleNumericChange('monthlyAttrition', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-404" data-mp="frmxujv6wy4k" />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-405" data-mp="1xe1itmvwe70j">
                            %
                          </span>
                        </div>
                      </div>}

                    {inputs.revenueCadence === 'quarterly' && <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-406" data-mp="3dtus4kwqquzh">
                        <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-407" data-mp="3irwb75u62uay">
                          Quarterly Attrition
                        </label>
                        <div className="w-56 relative" data-id="element-408" data-mp="3nwggti8cvnqr">
                          <input type="number" min="0" max="100" step="1" value={inputs.quarterlyAttrition || 5} onChange={e => handleNumericChange('quarterlyAttrition', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-409" data-mp="3ny1fnk1pdsuf" />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-410" data-mp="2q8zni9r39zkf">
                            %
                          </span>
                        </div>
                      </div>}
                    <CommissionInputs inputs={inputs} onChange={handleNumericChange} onSet={handleChange} variant="inline" data-id="element-411" data-mp="u6mibebos4o0" />
                  </>}

                {!managerView(inputs) && <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-412" data-mp="184taqexv9cga">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-413" data-mp="1d3nqf80jekw6">
                    {commissionsModeled(inputs) ? 'Margin Before Commissions' : 'Gross Profit Margin'}
                  </label>
                  <div className="w-56 relative" data-id="element-414" data-mp="1rtbtadod9rm0">
                    <input type="number" min="0" max="100" step="1" value={inputs.gpm} onChange={e => handleNumericChange('gpm', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-415" data-mp="1rumvwa62stbj" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-416" data-mp="32erf3qpbrnb0">
                      %
                    </span>
                  </div>
                </div>}
              </div>
            </div>}
        </div>

        {showActionButtons && onReset && <>
            <div className="border-t-2 border-gray-200 pt-4" data-id="element-417" data-mp="3lckieu3v1z8x">
              <button onClick={onReset} className="w-full px-4 py-2.5 rounded border-2 border-gray-300 text-gray-700 hover:border-gray-400 font-bold text-sm transition-colors active:translate-y-[1px]" data-id="element-418" data-mp="3l9ya2s95bfpn">
                Reset to Defaults
              </button>
            </div>

            <div className="text-sm text-gray-600 leading-relaxed border-t-2 border-gray-200 pt-4" data-id="element-419" data-mp="6enxcupq1erc">
              LMS note: access to a Learning Management System is included,
              reinforcing hands-on execution.
            </div>
          </>}

        {onReview && <div className="border-t-2 border-gray-200 pt-4" data-id="element-420" data-mp="3tlggm3y7ikk2">
            <button onClick={onReview} className="w-full py-3.5 md:py-4 rounded-lg font-extrabold text-base md:text-lg transition-all shadow-lg bg-amber hover:bg-amber-dark text-navy active:translate-y-[1px] cursor-pointer" data-id="element-421" data-mp="l4bzfy5q8hg4">
              Review Your Numbers →
            </button>
          </div>}
      </div>
    </div>;
}